/**
 * @module app.um
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api, ApiQuery } from 'app/shared/lib/essette-api';
import { IApiQueryResults, IApiActionResults, IQueryLoadOptions, IApiSettingsResults, IApiBatchProgress, MethodOptions, IActionInfo, ActionOptions } from 'app/shared/lib/api/api-types';
import { IMemberSearchFilters, countMemberFilters } from 'app/cd/services/member-service';
import { countProviderFilters, IProviderSearchFilters } from 'app/cd/services/provider-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { Toaster } from 'app/shared/lib/toaster';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { ApiDate, ApiDateTime } from 'app/shared/models/essette-api';
import { IApiFilterResults } from 'app/core/models/api-results';
import { IServiceSearchFilters } from 'app/cd/services/lookup-service';
import { IApplyAuthServicePayload } from 'app/um/components/auth/service-grid';
import { IApplyAuthMedicationPayload } from 'app/um/components/auth/medications/auth-medications-grid';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { IServiceChange, IMedicationChange } from 'app/pp/components/update/update-auth-details-step';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface IAuthSearchFilters {
    assignedTo?: Core.User | null;
    authNumber?: string | null;
    class?: UM.AuthClass | null;
    closedOnly?: boolean | null;
    detailFilter?: string | null;
    dueDateEnd?: string | null;
    dueDateStart?: string | null;
    isPending?: 'Complete' | 'Incomplete' | null;
    mine?: boolean | null;
    nextReviewDateEnd?: string | null;
    nextReviewDateStart?: string | null;
    openOnly?: boolean | null;
    overDue?: boolean | null;
    providerLastName?: string | null;
    providerServicingLocationProvider?: CD.Provider | null;
    providerType?: CD.AuthProviderType | null;
    requestedDateEnd?: string | null;
    requestedDateStart?: string | null;
    status?: UM.AuthStatus | null;
    statusReason?: UM.AuthStatusReason | null;
    subClass?: UM.AuthSubClass | null;
    type?: UM.AuthType | null;
}
export interface IAuthStatusSearchFilters {
    allowEditsInProviderPortal?: boolean | null;
    allowForDeterminationRules?: boolean | null;
    allowNullDateOfService?: boolean | null;
    allowStatusDateEdit?: boolean | null;
    dataEntryComplete?: boolean | null;
    determination?: {
        result?: {
            approved?: boolean | null;
            denied?: boolean | null;
            modified?: boolean | null;
            other?: boolean | null;
        }
    };
    excludeFromExport?: boolean | null;
    isApproval?: boolean | null;
    isDetermination?: boolean | null;
    isInitial?: boolean | null;
    isLocked?: boolean | null;
    isOpen?: boolean | null;
    isTurnAroundTimeBase?: boolean | null;
    isVoided?: boolean | null;
    providerChangesRequested?: boolean | null;
    requiresConfirmation?: boolean | null;
    sendToClaimsSystem?: boolean | null;
    status?: string | null;
    transitions?: UM.AuthStatus[] | null;
    withReasons?: boolean | null;
}

export interface IAuthServiceStatusSearchFilters {
    allowForDeterminationRules?: boolean | null;
    allowStatusDateEdit?: boolean | null;
    determination?: {
        statuses?: UM.AuthStatus[] | null;
        result?: {
            approved?: boolean | null;
            denied?: boolean | null;
            modified?: boolean | null;
            other?: boolean | null;
        }
    };
    excludeFromExport?: boolean | null;
    isApproval?: boolean | null;
    isDetermination?: boolean | null;
    isInitial?: boolean | null;
    isLocked?: boolean | null;
    isOpen?: boolean | null;
    isTurnAroundTimeBase?: boolean | null;
    isVoided?: boolean | null;
    // when true, not every flag criteria must match, just one
    multiFlagFilter?: boolean | null;
    providerChangesRequested?: boolean | null;
    requiresConfirmation?: boolean | null;
    sendToClaimsSystem?: boolean | null;
    status?: string | null;
    transitions?: UM.AuthServiceStatus[] | null;
    withReasons?: boolean | null;
}

export interface IAuthMedicationStatusSearchFilters {
    determination?: {
        statuses?: UM.AuthStatus[] | null;
        result?: {
            approved?: boolean | null;
            denied?: boolean | null;
            modified?: boolean | null;
            other?: boolean | null;
        }
    };
    isInitial?: boolean | null;
    isOpen?: boolean | null;
    isApproval?: boolean | null;
    isVoided?: boolean | null;
    isTurnAroundTimeBase?: boolean | null;
    isDetermination?: boolean | null;
    // when true, not every flag criteria must match, just one
    multiFlagFilter?: boolean | null;
    status?: string | null;
    transitions?: UM.AuthMedicationStatus[] | null;
    withReasons?: boolean | null;
}

export interface IAuthServiceHistorySearchFilters {
    onlyServices?: boolean | null;
    onlyDentalServices?: boolean | null;
    serviceId?: string | null;
}

export interface IAuthServiceStatusHistorySearchFilters {
    onlyServices?: boolean | null;
    onlyDentalServices?: boolean | null;
    serviceId?: string | null;
}

export interface IAuthMedicationHistorySearchFilters {
    medicationId?: string | null;
}

export interface IAuthMedicationStatusHistorySearchFilters {
    medicationId?: string | null;
}

export interface IAuthServiceHistorySearchFilters {
    onlyServices?: boolean | null;
    onlyDentalServices?: boolean | null;
    serviceId?: string | null;
}

export interface ITurnAroundTimeSearchFilters {
    lineOfBusiness?: UM.AuthLineOfBusiness | null;
    priority?: UM.AuthPriority | null;
    type?: UM.AuthType | null;
}

export interface IAuthMedicalReviewHistoryFilters {
    authId?: string | null;
    authServiceId?: string | null;
    authDiagnosisId?: string | null;
}

/** Returns the number of defined filters. Useful for checking that X filters exist before running a search */
export function countAuthFilters(searchFilters: IAuthSearchFilters): number {
    if(!searchFilters) { return 0; }

    let count = 0;
    if(searchFilters.authNumber) count += 1;
    if(searchFilters.type) count += 1;
    if(searchFilters.class) count += 1;
    if(searchFilters.subClass) count += 1;
    if(searchFilters.status) count += 1;
    if(searchFilters.statusReason) count += 1;
    if(searchFilters.assignedTo) count += 1;
    if(searchFilters.requestedDateStart) count += 1;
    if(searchFilters.requestedDateEnd) count += 1;
    if(searchFilters.dueDateStart) count += 1;
    if(searchFilters.dueDateEnd) count += 1;
    if(searchFilters.providerServicingLocationProvider) count += 1;
    if(searchFilters.detailFilter) count += 1;
    if(searchFilters.nextReviewDateStart) count += 1;
    if(searchFilters.nextReviewDateEnd) count += 1;
    if(searchFilters.providerType) count += 1;
    if(searchFilters.providerLastName) count += 1;
    if(!isNullOrUndefined(searchFilters.overDue)) count += 1;
    if(!isNullOrUndefined(searchFilters.mine)) count += 1;
    if(!isNullOrUndefined(searchFilters.openOnly)) count += 1;
    if(!isNullOrUndefined(searchFilters.closedOnly)) count += 1;
    if(!isNullOrUndefined(searchFilters.isPending)) count += 1;

    return count;
}

/** Returns the number of defined filters. Useful for checking that X filters exist before running a search */
export function countAuthStatusFilters(searchFilters: IAuthStatusSearchFilters): number {
    if(!searchFilters) return 0;

    let count = 0;
    if(searchFilters.status && searchFilters.status.trim().length > 0) count += 1;
    if(searchFilters.determination?.result?.approved) count += 1;
    if(searchFilters.determination?.result?.denied) count += 1;
    if(searchFilters.determination?.result?.modified) count += 1;
    if(searchFilters.determination?.result?.other) count += 1;
    if(searchFilters.allowEditsInProviderPortal) count += 1;
    if(searchFilters.allowStatusDateEdit) count += 1;
    if(searchFilters.allowNullDateOfService) count += 1;
    if(searchFilters.isApproval) count += 1;
    if(searchFilters.isOpen) count += 1;
    if(searchFilters.isTurnAroundTimeBase) count += 1;
    if(searchFilters.isVoided) count += 1;
    if(searchFilters.transitions?.length) count += 1;

    return count;
}

@autoinject()
@transient()
export class AuthService {

    ////****************** Functions ************************************************////
    constructor(
        private readonly api: Api,
        private readonly env: EnvironmentService,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster) {

        this.logger.source = this;
    }

    public countAuthFilters(authFilters: IAuthSearchFilters): number {
        return countAuthFilters(authFilters);
    }
    public countMemberFilters(memberFilters: IMemberSearchFilters): number {
        return countMemberFilters(memberFilters);
    }

    public async get<T>(query: ApiQuery): Promise<IApiQueryResults<T>> {
        return await this.api.get(query) as IApiQueryResults<T>;
    }


    ////****************** Settings *************************************************////
    public async loadAuthSettings(authId?: string | null, includes: string[] = []): Promise<IApiSettingsResults> {
        if(authId) {
            return this.api.get(
                this.api.settings(`um/auths/${authId}`).include(...includes)
            );
        } else {
            return this.api.get(
                this.api.settings(`um/auths`).include(...includes)
            );
        }
    }


    ////****************** Queries **************************************************////
    public createLoadAuthsQuery(options?: Partial<IQueryLoadOptions>, includes: string[] = []): ApiQuery {
        const query = this.api
            .query('um/auths')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return query;
    }
    public async loadAuths(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.createLoadAuthsQuery(options, includes);

        return await this.api.get(query);
    }

    public async searchAuths(
        authSearchFilters: IAuthSearchFilters,
        memberSearchFilters: IMemberSearchFilters,
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = [],
        includeWithInactiveMemberPlans: boolean = false): Promise<IApiQueryResults<UM.Auth>> {

        const portalSearch = this.env.application.code === 'Portal.Providers';

        if(!authSearchFilters) { authSearchFilters = {}; }
        if(!memberSearchFilters) { memberSearchFilters = {}; }

        const authFilterCount = countAuthFilters(authSearchFilters);
        const memberFilterCount = countMemberFilters(memberSearchFilters);
        if(authFilterCount + memberFilterCount === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria();
            return createQueryResults('UM.Auth', [], true);
        }

        const query = this.api
            .query('um/auths')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(includeWithInactiveMemberPlans) {
            query.filterBoolean('isActive', true)
            query.includeInactive(includeWithInactiveMemberPlans);
        }

        if(isNullOrUndefined(authSearchFilters.isPending)) {
            if(portalSearch) {
                // Find auths where isPending is false OR have been submitted by the context provider (pended or not)
                query.filterOr(query => query
                    .filterBoolean('isPending', false)
                    .filterSession('submittedBy.provider.id', '$context.id', 'CD.Provider')
                );
            }
            else if(!authSearchFilters.authNumber) {
                // Filter out pending auths unless auth number was searched by
                query.filterBoolean('isPending', false);
            }
        }
        else if(authSearchFilters.isPending === 'Complete') {
            query.filterBoolean('isPending', false);
        }
        else if(authSearchFilters.isPending === 'Incomplete') {
            query.filterBoolean('isPending', true);
            if(portalSearch) {
                query.filterSession('submittedBy.provider.id', '$context.id', 'CD.Provider')
            }
        }

        if(authFilterCount > 0) {
            if(!!authSearchFilters.mine && this.env.user.id) {
                if(authSearchFilters.assignedTo && authSearchFilters.assignedTo.id !== this.env.user.id) {
                    authSearchFilters.assignedTo = <Core.User>this.env.user;
                    authSearchFilters.assignedTo.$display = this.env.user.description;
                }
            }
            if(!!authSearchFilters.openOnly) {
                query.filterBoolean('isClosed', false);
            }
            else if(!!authSearchFilters.closedOnly) {
                query.filterBoolean('isClosed', true);
            }

            // TODO: date one
            if(authSearchFilters.overDue) {
                query.filterDateTimeSpecial('dueDateTime', 'now', 'lt');
                query.filterBoolean("isClosed", false);
            }
            if(authSearchFilters.authNumber) {
                // Use an or search to look at auth number or other reference number
                query.filterOr(query => query
                    .filterString('number', authSearchFilters.authNumber!, 'startsWith')
                    .filterString('alternateNumber', authSearchFilters.authNumber!, 'startsWith')
                );
            }
            if(authSearchFilters.detailFilter) {
                // Use an or search to look at various diagnoses, service, and medication data
                query.filterOr(query => query
                    .filterExists('services', (f =>
                        f.filterString('definition.code', authSearchFilters.detailFilter!, 'contains')
                    ))
                    .filterExists('services', (f =>
                        f.filterString('definition.description', authSearchFilters.detailFilter!, 'contains')
                    ))
                    .filterExists('diagnoses', (f =>
                        f.filterString('definition.code', authSearchFilters.detailFilter!, 'contains')
                    ))
                    .filterExists('diagnoses', (f =>
                        f.filterString('definition.description', authSearchFilters.detailFilter!, 'contains')
                    ))
                    .filterExists('medications', (f =>
                        f.filterString('definition.code', authSearchFilters.detailFilter!, 'contains')
                    ))
                    .filterExists('medications', (f =>
                        f.filterString('definition.description', authSearchFilters.detailFilter!, 'contains')
                    )));
            }
            if(authSearchFilters.assignedTo?.id) {
                query.overrideUrl(`um/auths/owners/${authSearchFilters.assignedTo.id}/auths`);
            } else if(authSearchFilters.assignedTo?.id === '') {
                query.filterIsNull('assignedTo.id');
            }
            if(authSearchFilters.type?.id) {
                query.filterString('type.id', authSearchFilters.type.id);
            }
            if(authSearchFilters.class?.id) {
                query.filterString('class.id', authSearchFilters.class.id);
            }
            if(authSearchFilters.subClass?.id) {
                query.filterString('class.subclass.id', authSearchFilters.subClass.id);
            }
            if(authSearchFilters.status?.id) {
                query.filterString('status.id', authSearchFilters.status.id);
            }
            if(authSearchFilters.statusReason?.id) {
                query.filterString('status.reason.id', authSearchFilters.statusReason.id);
            }
            if(authSearchFilters.dueDateStart) {
                query.filterDateTimeBetweenDates(
                    'dueDateTime',
                    authSearchFilters.dueDateStart,
                    authSearchFilters.dueDateEnd || null);
            }
            if(authSearchFilters.requestedDateStart) {
                query.filterDateTimeBetweenDates(
                    'requestedDateTime',
                    authSearchFilters.requestedDateStart,
                    authSearchFilters.requestedDateEnd || null);
            }
            if(authSearchFilters.nextReviewDateStart) {
                query.filterDateBetween(
                    'nextReviewDate',
                    authSearchFilters.nextReviewDateStart,
                    authSearchFilters.nextReviewDateEnd || null);
            }
            if(authSearchFilters.providerServicingLocationProvider?.id) {
                query.filterString('provider.servicing.location.provider.id', authSearchFilters.providerServicingLocationProvider.id);
            }
            if(authSearchFilters.providerType?.id && authSearchFilters.providerLastName) {
                query.filterExists('providers', f =>
                    f.filterAnd(and => and
                        .filterString('name.last', authSearchFilters.providerLastName!, 'startsWith')
                        .filterIdentifier('detail.type.id', authSearchFilters.providerType!.id!)
                    )
                );
            } else if(authSearchFilters.providerType?.id) {
                query.filterExists('providers', f =>
                    f.filterIdentifier('detail.type.id', authSearchFilters.providerType!.id!)
                );
            } else if(authSearchFilters.providerLastName) {
                query.filterExists('providers', f =>
                    f.filterString('name.last', authSearchFilters.providerLastName!, 'startsWith')
                );
            }
        }

        if(memberFilterCount > 0) {
            if(memberSearchFilters.id) {
                // use member ID when filtering auths on M360 Summary
                query.filterIdentifier('member.id', memberSearchFilters.id);
            }
            if(memberSearchFilters.memberId) {
                // search by plan identifier since Auths are tied to individual plans
                query.filterString('member.plan.identifier.primary', memberSearchFilters.memberId, 'startsWith');
            }
            if(memberSearchFilters.medicaidId) {
                query.filterString('member.identifier.medicaid', memberSearchFilters.medicaidId, 'startsWith');
            }
            if(memberSearchFilters.ssn) {
                // strip out hyphens initially since users might put hyphens any old place
                let hyphenSsn = memberSearchFilters.ssn;
                const integerSsn = hyphenSsn.replace(/-/g, '');
                // if user input did not include hyphens, build a properly hyphenated variant
                if(hyphenSsn === integerSsn) {
                    if(integerSsn.length > 3 && integerSsn.length <= 5) hyphenSsn = `${integerSsn.slice(0, 3)}-${integerSsn.slice(3)}`;
                    else if(integerSsn.length > 5) hyphenSsn = `${integerSsn.slice(0, 3)}-${integerSsn.slice(3, 5)}-${integerSsn.slice(5)}`;
                }
                // if the variants are still the same
                if(hyphenSsn === integerSsn) {
                    query.filterString('member.identifier.socialSecurityNumber', integerSsn, 'startsWith')
                } else {
                    // otherwise, search by both
                    query.filterOr(query => query
                        .filterString('member.identifier.socialSecurityNumber', integerSsn, 'startsWith')
                        .filterString('member.identifier.socialSecurityNumber', hyphenSsn, 'startsWith')
                    );
                }
            }
            if(memberSearchFilters.memberLastName) {
                query.filterString('member.name.last', memberSearchFilters.memberLastName, 'startsWith');
            }
            if(memberSearchFilters.memberFirstName) {
                query.filterString('member.name.first', memberSearchFilters.memberFirstName, 'startsWith');
            }
            if(memberSearchFilters.dob) {
                query.filterDate('member.birthDate', memberSearchFilters.dob);
            }
            if(memberSearchFilters.dobBetweenStart) {
                query.filterDateTimeBetweenDates(
                    'member.birthDate',
                    memberSearchFilters.dobBetweenStart,
                    memberSearchFilters.dobBetweenEnd || null);
            }
            if(memberSearchFilters.eligibility) {
                query.filterBoolean('member.eligibility.isCurrent', true);
            }
            if(memberSearchFilters.memberPlan) {
                query.filterString('member.plan.id', memberSearchFilters.memberPlan.id || null)
            }
            if(memberSearchFilters.healthPlan?.id) {
                query.filterIdentifier('member.plan.healthPlan.id', memberSearchFilters.healthPlan.id);
            }
            if(memberSearchFilters.lineOfBusiness?.id) {
                query.filterIdentifier('member.plan.lineOfBusiness.id', memberSearchFilters.lineOfBusiness.id);
            }
            if(memberSearchFilters.pcpId) {
                query.filterString('member.primaryCarePhysician.identifier.primary', memberSearchFilters.pcpId, 'startsWith');
            }
            if(memberSearchFilters.pcpFirstName) {
                query.filterString('member.primaryCarePhysician.name.first', memberSearchFilters.pcpFirstName, 'startsWith');
            }
            if(memberSearchFilters.pcpLastName) {
                query.filterString('member.primaryCarePhysician.name.last', memberSearchFilters.pcpLastName, 'startsWith');
            }
        }

        return await this.api.get(query);
    }

    public async searchAuthQueues(
        queueId: string,
        authSearchFilters: IAuthSearchFilters,
        providerSearchFilters: IProviderSearchFilters,
        options?: Partial<IQueryLoadOptions>,
        usePortal: boolean = false
    ): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query(`um/${usePortal ? 'portal' : 'auths'}/queues/${queueId}/auths`)
            .applyStandardLoadOptions(options)
            .field('id');


        if(!authSearchFilters) authSearchFilters = {};
        if(!providerSearchFilters) providerSearchFilters = {};
        const authFilterCount = countAuthFilters(authSearchFilters);
        const providerFilterCount = countProviderFilters(providerSearchFilters);

        if(authFilterCount + providerFilterCount) {
            query.strategy('search');
            if(authFilterCount > 0) {
                if(authSearchFilters.assignedTo && authSearchFilters.assignedTo.id) {
                    query.filterIdentifier('assignedTo.id', authSearchFilters.assignedTo.id);
                } else if(authSearchFilters.assignedTo && authSearchFilters.assignedTo.id === '') {
                    query.filterIsNull('assignedTo.id');
                }
                let filterClosed = false;
                if(!!authSearchFilters.openOnly) {
                    filterClosed = true;
                } else if(!!authSearchFilters.closedOnly) {
                    query.filterBoolean('isClosed', true);
                }
                if(authSearchFilters.overDue) {
                    query.filterDateTimeSpecial('dueDateTime', 'now', 'lt');
                    filterClosed = true;
                }
                if(filterClosed) query.filterBoolean('isClosed', false);
                if(authSearchFilters.authNumber) {
                    query.filterOr(query => query
                        .filterString('number', authSearchFilters.authNumber!, 'startsWith')
                        .filterString('alternateNumber', authSearchFilters.authNumber!, 'startsWith')
                    );
                }
                if(authSearchFilters.type && authSearchFilters.type.id) {
                    query.filterString('type.id', authSearchFilters.type.id || null);
                }
                if(authSearchFilters.class && authSearchFilters.class.id) {
                    query.filterString('class.id', authSearchFilters.class.id || null);
                }
                if(authSearchFilters.subClass && authSearchFilters.subClass.id) {
                    query.filterString('class.subclass.id', authSearchFilters.subClass.id || null);
                }
                if(authSearchFilters.status && authSearchFilters.status.id) {
                    query.filterString('status.id', authSearchFilters.status.id || null);
                }
                if(authSearchFilters.statusReason) {
                    query.filterString('status.reason.id', authSearchFilters.statusReason.id || null);
                }
                if(authSearchFilters.dueDateStart) {
                    query.filterDateTimeBetweenDates(
                        'dueDateTime',
                        authSearchFilters.dueDateStart,
                        authSearchFilters.dueDateEnd || null);
                }
                if(authSearchFilters.requestedDateStart) {
                    query.filterDateTimeBetweenDates(
                        'requestedDateTime',
                        authSearchFilters.requestedDateStart,
                        authSearchFilters.requestedDateEnd || null);
                }
            }

            if(providerFilterCount > 0) {
                if(providerFilterCount === 1 && providerSearchFilters.type && providerSearchFilters.type.id) {
                    query.filterExists('providers', f =>
                        f.filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                    );
                }
                if(providerSearchFilters.identifier) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterString('identifier.primary', providerSearchFilters.identifier!, 'startsWith')
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterString('identifier.primary', providerSearchFilters.identifier!, 'startsWith')
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.npi) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterOr(or => or
                                .filterString('identifier.nationalProvider', providerSearchFilters.npi!, 'startsWith')
                                .filterString('location.facility.identifier.nationalProvider', providerSearchFilters.npi!, 'startsWith')
                                .filterString('location.facility.group.identifier.nationalProvider', providerSearchFilters.npi!, 'startsWith')
                            )
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterOr(or => or
                                    .filterString('identifier.nationalProvider', providerSearchFilters.npi!, 'startsWith')
                                    .filterString('location.facility.identifier.nationalProvider', providerSearchFilters.npi!, 'startsWith')
                                    .filterString('location.facility.group.identifier.nationalProvider', providerSearchFilters.npi!, 'startsWith')
                                )
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.providerFirstName) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f => {
                            f.filterString('name.first', providerSearchFilters.providerFirstName!, 'startsWith');
                        });
                    } else {
                        query.filterExists('providers', f => {
                            f.filterAnd(and => and
                                .filterString('name.first', providerSearchFilters.providerFirstName!, 'startsWith')
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            );
                        });
                    }
                }
                if(providerSearchFilters.providerLastName) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f => {
                            f.filterString('name.last', providerSearchFilters.providerLastName!, 'startsWith');
                        });
                    } else {
                        query.filterExists('providers', f => {
                            f.filterAnd(and => and
                                .filterString('name.last', providerSearchFilters.providerLastName!, 'startsWith')
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            );
                        });
                    }
                }
                if(providerSearchFilters.specialty && providerSearchFilters.specialty.id) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers.specialties', (f =>
                            f.filterIdentifier('id', providerSearchFilters!.specialty!.id!)
                        ));
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterExists('specialties', (f => f.filterIdentifier('id', providerSearchFilters!.specialty!.id!)))
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.gender && providerSearchFilters.gender.id) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterIdentifier('gender.id', providerSearchFilters!.gender!.id!)
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterIdentifier('gender.id', providerSearchFilters!.gender!.id!)
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.facilityName) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterString('location.facility.description', providerSearchFilters.facilityName!, 'contains')
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterString('location.facility.description', providerSearchFilters.facilityName!, 'contains')
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.groupName) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterString('location.facility.group.description', providerSearchFilters.groupName!, 'contains')
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterString('location.facility.group.description', providerSearchFilters.groupName!, 'contains')
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.tax) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterString('location.facility.identifier.tax', providerSearchFilters.tax!, 'startsWith')
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterString('location.facility.identifier.tax', providerSearchFilters.tax!, 'startsWith')
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
                if(providerSearchFilters.facilityAddress) {
                    if(!providerSearchFilters.type?.id) {
                        query.filterExists('providers', f =>
                            f.filterExists('location.facility.addresses', f => {
                                if(!(providerSearchFilters && providerSearchFilters.facilityAddress)) return;
                                const a = providerSearchFilters.facilityAddress;
                                if(a.street1) f.filterString('street1', a.street1, 'contains');
                                if(a.street2) f.filterString('street2', a.street2, 'contains');
                                if(a.city) f.filterString('city', a.city, 'startsWith');
                                if(a.region && a.region.id) f.filterIdentifier('region.id', a.region.id);
                                if(a.postalCode) f.filterString('postalCode', a.postalCode, 'startsWith');
                            })
                        );
                    } else {
                        query.filterExists('providers', f =>
                            f.filterAnd(and => and
                                .filterExists('location.facility.addresses', f => {
                                    if(!(providerSearchFilters && providerSearchFilters.facilityAddress)) return;
                                    const a = providerSearchFilters.facilityAddress;
                                    if(a.street1) f.filterString('street1', a.street1, 'contains');
                                    if(a.street2) f.filterString('street2', a.street2, 'contains');
                                    if(a.city) f.filterString('city', a.city, 'startsWith');
                                    if(a.region && a.region.id) f.filterIdentifier('region.id', a.region.id);
                                    if(a.postalCode) f.filterString('postalCode', a.postalCode, 'startsWith');
                                })
                                .filterIdentifier('detail.type.id', providerSearchFilters.type!.id!)
                            )
                        );
                    }
                }
            }
        }

        return await this.api.get(query);
    }

    public async loadAuthsById(
        authIds: string[],
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = []): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query(`um/auths`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterIdentifierArray('id', authIds, 'eq');

        return await this.api.get(query);
    }

    public async loadAuthsByIdFromAuthQueue(
        queueId: string,
        authIds: string[],
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = [],
        usePortal: boolean = false
    ): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query(`um/${usePortal ? 'portal' : 'auths'}/queues/${queueId}/auths`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterIdentifierArray('id', authIds, 'eq');

        return await this.api.get(query);
    }


    public async loadAuthProviderTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthProviderType>> {
        const query = this.api
            .query('um/auths/providers/types')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthModifiers(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.AuthModifier>> {
        const query = this.api
            .query(`cd/auths/modifiers`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async searchAuthModifiers(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.AuthModifier>> {
        const query = this.api
            .query(`cd/auths/modifiers`)
            .search(searchValue)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthValidationResults(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthValidationResult>> {
        const query = this.api
            .query(`um/auths/${authId}/validation/results`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadAuthSubClassProviders(authSubClassId: string, authDate: ApiDateTime | ApiDate, options?: Partial<IQueryLoadOptions>, includes: string[] = [], current: boolean = true): Promise<IApiQueryResults<UM.AuthProviderType>> {
        const query = this.api
            .query(`um/auths/subclasses/${authSubClassId}/providerTypes`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        //.filterDirect(`(date'${authDate.toISOString().substring(0, 10)}' in detail.dateRange) or (detail.dateRange.end eq null and date'${authDate.toISOString().substring(0, 10)}' gte detail.dateRange.start)`)
        if(current) {
            query.filterDateTime('detail.dateRange.start', authDate, 'lte');
            query.filterOr((or) => {
                or.filterIsNull('detail.dateRange.end', true);
                or.filterDateTime('detail.dateRange.end', authDate, 'gt');
            });
        }

        return await this.api.get(query);
    }

    public async loadAuthSubClassObjects(authSubClassId: string, authDate: ApiDateTime | ApiDate, options?: Partial<IQueryLoadOptions>, includes: string[] = [], current: boolean = true): Promise<IApiQueryResults<UM.AuthObject>> {
        const query = this.api
            .query(`um/auths/subclasses/${authSubClassId}/objects`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        //.filterDirect(`(date'${authDate.toISOString().substring(0, 10)}' in detail.dateRange) or (detail.dateRange.end eq null and date'${authDate.toISOString().substring(0, 10)}' gte detail.dateRange.start)`)
        if(current) {
            query.filterDateTime('detail.dateRange.start', authDate, 'lte');
            query.filterOr((or) => {
                or.filterIsNull('detail.dateRange.end', true);
                or.filterDateTime('detail.dateRange.end', authDate, 'gt');
            });
        }

        return await this.api.get(query);
    }

    public async loadAuthSubClassResources(authSubClassId: string, options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<Core.Resource>> {
        const query = this.api
            .query(`um/auths/subclasses/${authSubClassId}/resources`)
            .applyStandardLoadOptions(options)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadAuthSubClassAssessments(authSubClassId: string, authDate: ApiDateTime | ApiDate, options?: Partial<IQueryLoadOptions>, includes: string[] = [], current: boolean = true, required: boolean | null = null): Promise<IApiQueryResults<UM.AuthAssessment>> {
        const query = this.api
            .query(`um/auths/subclasses/${authSubClassId}/assessments`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(required !== null) {
            query.filterBoolean('detail.isOptional', !required);
        }

        if(current) {
            query.filterDateTime('detail.dateRange.start', authDate, 'lte');
            query.filterOr((or) => {
                or.filterIsNull('detail.dateRange.end', true);
                or.filterDateTime('detail.dateRange.end', authDate, 'gt');
            });
        }

        return await this.api.get(query);
    }

    public async loadAuthSources(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthSource>> {
        const query = this.api
            .query('um/auths/sources')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAdmissionSources(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AdmissionSource>> {
        const query = this.api
            .query('um/admissions/sources')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthFlags(options?: Partial<IQueryLoadOptions>, includes: string[] = [], type: string = ''): Promise<IApiQueryResults<UM.AuthFlag>> {
        const query = this.api
            .query('um/auths/flags')
            .applyStandardLoadOptions(options)
            .include(...includes);
        if(type === 'services') {
            query.filterBoolean('flag.allowsServices', true);
        } else if(type === 'diagnoses') {
            query.filterBoolean('flag.allowsDiagnoses', true);
        }

        return await this.api.get(query);
    }

    public async loadAuthPriorities(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthPriority>> {
        const query = this.api
            .query('um/auths/priorities')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadHealthcareServiceTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.HealthcareServiceType>> {
        const query = this.api
            .query('um/healthcareServices/types')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthLinesOfBusiness(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthLineOfBusiness>> {
        const query = this.api
            .query('um/auths/linesOfBusiness')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadAuthServiceTurnAroundTimes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], searchFilters: ITurnAroundTimeSearchFilters = {}): Promise<IApiQueryResults<UM.AuthServiceTurnAroundTime>> {
        const query = this.api
            .query('um/auths/services/turnAroundTimes')
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(searchFilters.lineOfBusiness?.id) {
            query.filterIdentifier('lineOfBusiness.id', searchFilters.lineOfBusiness.id);
        }
        if(searchFilters.priority?.id) {
            query.filterIdentifier('priority.id', searchFilters.priority.id);
        }
        if(searchFilters.type?.id) {
            query.filterIdentifier('type.id', searchFilters.type.id);
        }

        return await this.api.get(query);
    }

    public async loadAuthRequestedOverrideReasons(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthRequestedOverrideReason>> {
        const query = this.api
            .query('um/auths/overrides/requestedreasons')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadDiagnosesByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthDiagnosis>> {
        const query = this.api
            .query(`um/auths/${authId}/diagnoses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadDiagnosesByAuthFlag(authFlagId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Diagnosis>> {
        const query = this.api
            .query(`um/auths/flags/${authFlagId}/diagnoses`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadMedicationsByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthMedication>> {
        const query = this.api
            .query(`um/auths/${authId}/medications`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadServicesByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], serviceSearchFilters?: IServiceSearchFilters, fields: string[] = []): Promise<IApiQueryResults<UM.AuthService>> {
        const query = this.api
            .query(`um/auths/${authId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(serviceSearchFilters) {
            if(serviceSearchFilters.isDental) {
                query.filterString('definition.type.bodySiteType.code', 'Dental', 'eq');
            } else {
                query.filterOr(query => {
                    query.filterString('definition.type.bodySiteType.code', 'Dental', 'ne');
                    query.filterIsNull('definition.type.bodySiteType.code');
                });
            }
        }

        return await this.api.get(query);
    }

    public async loadFilteredAuthServices(authId: string, authMemberPlanId: string, services: UM.AuthService[], options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthService>> {
        const query = this.api
            .query('um/auths/services')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        query.filterIdentifier('auth.id', authId, 'ne');
        query.filterIdentifier('auth.member.plan.id', authMemberPlanId, 'eq');
        const serviceDefinitionIds: string[] = [];
        services.forEach(service => {
            if(service.definition?.id) serviceDefinitionIds.push(service.definition.id);
        })

        query.filterIdentifierArray('definition.id', serviceDefinitionIds, 'eq');

        return await this.api.get(query);
    }

    public async loadServicesByAuthFlag(authFlagId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Service>> {
        const query = this.api
            .query(`um/auths/flags/${authFlagId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadServicesByFlag(flagId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Service>> {
        const query = this.api
            .query(`um/auths/flags/${flagId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadServiceStatusHistory(serviceId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthServiceStatusHistory>> {
        const query = this.api
            .query(`um/auths/history/services/statuses`)
            .filterIdentifier('service.id', serviceId)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadServiceStatusHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], authServiceStatusHistoryFilters?: IAuthServiceStatusHistorySearchFilters): Promise<IApiQueryResults<UM.AuthServiceStatusHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/services/statuses`)
            .applyStandardLoadOptions(options)
            .includeWithState(options, 'status')
            .include(...includes)
            .field(...fields);

        if(authServiceStatusHistoryFilters?.onlyServices) {
            query.filterOr(or => {                    // exclude dental services
                or.filterString('service.definition.type.bodySiteType.code', 'Dental', 'ne');
                or.filterIsNull('service.definition.type.bodySiteType.code');
            });
        }

        if(authServiceStatusHistoryFilters?.onlyDentalServices) {
            query.filterString('service.definition.type.bodySiteType.code', 'Dental', 'eq');
        }

        if(authServiceStatusHistoryFilters?.serviceId) {
            query.filterIdentifier('service.id', authServiceStatusHistoryFilters.serviceId);
        }

        return await this.api.get(query);
    }

    public async loadAuthServiceHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], authServiceHistoryFilters?: IAuthServiceHistorySearchFilters, fields: string[] = []): Promise<IApiQueryResults<UM.AuthServiceHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/services`)
            .applyStandardLoadOptions(options)
            .includeWithState(options, 'service')
            .include(...includes)
            .field(...fields);

        if(authServiceHistoryFilters?.onlyServices) {
            query.filterOr(or => {                    // exclude dental services
                or.filterString('service.definition.type.bodySiteType.code', 'Dental', 'ne');
                or.filterIsNull('service.definition.type.bodySiteType.code');
            });
        }

        if(authServiceHistoryFilters?.onlyDentalServices) {
            query.filterString('service.definition.type.bodySiteType.code', 'Dental', 'eq');
        }

        if(authServiceHistoryFilters?.serviceId) {
            query.filterIdentifier('service.id', authServiceHistoryFilters.serviceId);
        }

        return await this.api.get(query);
    }

    public async loadAuthMedicationHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], authMedicationHistoryFilters?: IAuthMedicationHistorySearchFilters): Promise<IApiQueryResults<UM.AuthMedicationHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/medications`)
            .applyStandardLoadOptions(options)
            .includeWithState(options, 'medication')
            .include(...includes)
            .field(...fields);

        if(authMedicationHistoryFilters?.medicationId) {
            query.filterIdentifier('medication.id', authMedicationHistoryFilters.medicationId);
        }

        return await this.api.get(query);
    }

    public async loadAuthMedicationStatusHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], authMedicationStatusHistoryFilters?: IAuthMedicationStatusHistorySearchFilters): Promise<IApiQueryResults<UM.AuthMedicationStatusHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/medications/statuses`)
            .applyStandardLoadOptions(options)
            .includeWithState(options, 'status')
            .include(...includes)
            .field(...fields);

        if(authMedicationStatusHistoryFilters?.medicationId) {
            query.filterIdentifier('medication.id', authMedicationStatusHistoryFilters.medicationId);
        }

        return await this.api.get(query);
    }

    public async loadDeterminationsByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthDeterminationHistory>> {
        includes.push('authStatus');
        includes.push('determination.source');
        includes.push('determination.user');

        const query = this.api
            .query('um/auths/history/determinations')
            .applyStandardLoadOptions(options)
            .filterIdentifier('auth.id', authId)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadDeterminationDetails(determinationHistoryId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthDeterminationHistoryDetail>> {
        if(!includes.includes('authServiceStatus')) includes.push('authServiceStatus');
        if(!includes.includes('medicationStatus')) includes.push('medicationStatus');

        const query = this.api
            .query('um/auths/history/determination/details')
            .applyStandardLoadOptions(options)
            .filterIdentifier('determinationHistory.id', determinationHistoryId)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadServiceDateHistory(authId: string, options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<UM.AuthServiceDateHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/serviceDates`)
            .applyStandardLoadOptions(options)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthServiceDateHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthServiceDateHistory>> {
        const query = this.api
            .query(`um/auths/history/serviceDates`)
            .applyStandardLoadOptions(options)
            .filterIdentifier('auth.id', authId)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadCommonProvidersBySubClass(subClassId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.CommonProvider>> {
        const query = this.api
            .query('um/commonProviders')
            .applyStandardLoadOptions(options)
            .filterIdentifier('subclass.id', subClassId)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadCommonProvidersWithAdmission(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.CommonProvider>> {
        const query = this.api
            .query('um/commonProviders')
            .applyStandardLoadOptions(options)
            .filterBoolean('subclass.class.isAdmission', true)
            .filterBoolean('providerLocation.isActive', true)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadCommonDiagnoses(options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<UM.CommonDiagnosis>> {
        const query = this.api
            .query('um/commonDiagnoses')
            .applyStandardLoadOptions(options)
            .include('diagnosis')
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadCommonServices(options?: Partial<IQueryLoadOptions>, includes: string[] = [], serviceSearchFilters?: IServiceSearchFilters, fields: string[] = []): Promise<IApiQueryResults<UM.CommonService>> {
        if(!includes.includes('service')) includes.push('service');
        const query = this.api
            .query('um/commonServices')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(serviceSearchFilters) {
            if(serviceSearchFilters.isDental) {
                query.filterString('service.type.bodySiteType.code', 'Dental', 'eq');
            } else {
                query.filterOr(query => {
                    query.filterString('service.type.bodySiteType.code', 'Dental', 'ne');
                    query.filterIsNull('service.type.bodySiteType.code');
                });
            }
        }

        return await this.api.get(query);
    }

    public async loadAuthsByMember(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query(`um/members/${memberId}/auths`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthsCreatedByTask(taskId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query('um/auths')
            .applyStandardLoadOptions(options || null)
            .include(...includes)
            .filterIdentifier('metadata.created.byTask.id', taskId);

        return await this.api.get(query);
    }

    public async loadStatusHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthStatusHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/statuses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAssignmentHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthAssignmentHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/assignments`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthAssignmentHistory(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthAssignmentHistory>> {
        const query = this.api
            .query(`um/auths/history/assignments`)
            .filterIdentifier('auth.id', authId)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadQueues(options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeInactiveStatuses: boolean = false, fields: string[] = []): Promise<IApiQueryResults<UM.AuthQueue>> {
        const query = this.api
            .query('um/auths/queues')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(includeInactiveStatuses) {
            query.includeWithState('inactive', 'statuses');
        }
        return await this.api.get(query);
    }

    public createLoadAuthsByQueueQuery(queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): ApiQuery {
        const query = this.api
            .query(`um/auths/queues/${queueId}/auths`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return query;
    }

    public async loadPortalQueues(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], includeInactiveDetails: boolean = false, loadFallbackQueueOnly: boolean = false): Promise<IApiQueryResults<UM.PortalQueue>> {
        const query = this.api
            .query('um/portal/queues')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(includeInactiveDetails) {
            query.includeWithState('inactive', 'healthPlans', 'linesOfBusiness', 'authSubClasses');
        }

        if(loadFallbackQueueOnly) {
            query.filterBoolean('flag.isFallbackQueue', true).top(1);
        }

        return await this.api.get(query);
    }

    public async loadQueueHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthQueueHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/queues`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public createLoadAuthOwnersQuery(options?: Partial<IQueryLoadOptions>, includes: string[] = [], excludeEmpty: boolean = false, feature: string | null = null, fields: string[] = []): ApiQuery {
        const query = this.api
            .query('um/auths/owners')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(excludeEmpty) {
            query.filterDirect('openAuthCount gt 0');
        }

        if(feature) {
            query.feature(feature);
        }

        return query;
    }

    // Load auth owner information by by owner id
    public async loadAuthOwner(ownerId: string, includes: string[] = []): Promise<IApiQueryResults<UM.AuthOwner>> {
        const query = this.api
            .query(`um/auths/owners/${ownerId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    /** Loads users who have the auth owner permission
     * @param excludeEmpty true: only return auth owners that have at least one open auth
     */
    public async loadAuthOwners(options?: Partial<IQueryLoadOptions>, includes: string[] = [], excludeEmpty: boolean = false, feature: string | null = null, fields: string[] = []): Promise<IApiQueryResults<UM.AuthOwner>> {
        const query = this.createLoadAuthOwnersQuery(options, includes, excludeEmpty, feature, fields);
        return await this.api.get(query);
    }

    public async loadAuthStatuses(options?: Partial<IQueryLoadOptions>, includes: string[] = [], authStatusSearchFilters: IAuthStatusSearchFilters = {}, fields: string[] = [], multiFlagFilter: boolean = false): Promise<IApiQueryResults<UM.AuthStatus>> {

        const query = this.api
            .query('um/auths/statuses')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(authStatusSearchFilters.withReasons) {
            query.includeWithState(options, 'reasons')
                .filterExists('reasons');

            if(!includes.includes('reasons')) {
                includes.push('reasons');
            }
        }

        if(!multiFlagFilter) {
            if(!isNullOrUndefined(authStatusSearchFilters.isInitial)) query.filterBoolean('flag.isInitial', authStatusSearchFilters.isInitial);
            if(!isNullOrUndefined(authStatusSearchFilters.isOpen)) query.filterBoolean('flag.isOpen', authStatusSearchFilters.isOpen);
            if(!isNullOrUndefined(authStatusSearchFilters.isApproval)) query.filterBoolean('flag.isApproval', authStatusSearchFilters.isApproval);
            if(!isNullOrUndefined(authStatusSearchFilters.isLocked)) query.filterBoolean('flag.isLocked', authStatusSearchFilters.isLocked);
            if(!isNullOrUndefined(authStatusSearchFilters.requiresConfirmation)) query.filterBoolean('flag.requiresConfirmation', authStatusSearchFilters.requiresConfirmation);
            if(!isNullOrUndefined(authStatusSearchFilters.excludeFromExport)) query.filterBoolean('flag.excludeFromExport', authStatusSearchFilters.excludeFromExport);
            if(!isNullOrUndefined(authStatusSearchFilters.dataEntryComplete)) query.filterBoolean('flag.dataEntryComplete', authStatusSearchFilters.dataEntryComplete);
            if(!isNullOrUndefined(authStatusSearchFilters.allowForDeterminationRules)) query.filterBoolean('flag.allowForDeterminationRules', authStatusSearchFilters.allowForDeterminationRules);
            if(!isNullOrUndefined(authStatusSearchFilters.allowEditsInProviderPortal)) query.filterBoolean('flag.allowEditsInProviderPortal', authStatusSearchFilters.allowEditsInProviderPortal);
            if(!isNullOrUndefined(authStatusSearchFilters.allowNullDateOfService)) query.filterBoolean('flag.allowNullDateOfService', authStatusSearchFilters.allowNullDateOfService); // RAISE ISSUE
            if(!isNullOrUndefined(authStatusSearchFilters.isTurnAroundTimeBase)) query.filterBoolean('flag.isTurnAroundTimeBase', authStatusSearchFilters.isTurnAroundTimeBase);
            if(!isNullOrUndefined(authStatusSearchFilters.allowStatusDateEdit)) query.filterBoolean('flag.allowStatusDateEdit', authStatusSearchFilters.allowStatusDateEdit);
            if(!isNullOrUndefined(authStatusSearchFilters.isVoided)) query.filterBoolean('flag.isVoided', authStatusSearchFilters.isVoided);
            if(!isNullOrUndefined(authStatusSearchFilters.sendToClaimsSystem)) query.filterBoolean('flag.sendToClaimsSystem', authStatusSearchFilters.sendToClaimsSystem);
        } else {
            query.filterOr(or => {
                if(!isNullOrUndefined(authStatusSearchFilters.isInitial)) or.filterBoolean('flag.isInitial', authStatusSearchFilters.isInitial);
                if(!isNullOrUndefined(authStatusSearchFilters.isOpen)) or.filterBoolean('flag.isOpen', authStatusSearchFilters.isOpen);
                if(!isNullOrUndefined(authStatusSearchFilters.isApproval)) or.filterBoolean('flag.isApproval', authStatusSearchFilters.isApproval);
                if(!isNullOrUndefined(authStatusSearchFilters.isLocked)) or.filterBoolean('flag.isLocked', authStatusSearchFilters.isLocked);
                if(!isNullOrUndefined(authStatusSearchFilters.requiresConfirmation)) or.filterBoolean('flag.requiresConfirmation', authStatusSearchFilters.requiresConfirmation);
                if(!isNullOrUndefined(authStatusSearchFilters.excludeFromExport)) or.filterBoolean('flag.excludeFromExport', authStatusSearchFilters.excludeFromExport);
                if(!isNullOrUndefined(authStatusSearchFilters.dataEntryComplete)) or.filterBoolean('flag.dataEntryComplete', authStatusSearchFilters.dataEntryComplete);
                if(!isNullOrUndefined(authStatusSearchFilters.allowForDeterminationRules)) or.filterBoolean('flag.allowForDeterminationRules', authStatusSearchFilters.allowForDeterminationRules);
                if(!isNullOrUndefined(authStatusSearchFilters.allowEditsInProviderPortal)) or.filterBoolean('flag.allowEditsInProviderPortal', authStatusSearchFilters.allowEditsInProviderPortal);
                if(!isNullOrUndefined(authStatusSearchFilters.allowNullDateOfService)) or.filterBoolean('flag.allowNullDateOfService', authStatusSearchFilters.allowNullDateOfService); // RAISE ISSUE
                if(!isNullOrUndefined(authStatusSearchFilters.isTurnAroundTimeBase)) or.filterBoolean('flag.isTurnAroundTimeBase', authStatusSearchFilters.isTurnAroundTimeBase);
                if(!isNullOrUndefined(authStatusSearchFilters.allowStatusDateEdit)) or.filterBoolean('flag.allowStatusDateEdit', authStatusSearchFilters.allowStatusDateEdit);
                if(!isNullOrUndefined(authStatusSearchFilters.isVoided)) or.filterBoolean('flag.isVoided', authStatusSearchFilters.isVoided);
                if(!isNullOrUndefined(authStatusSearchFilters.sendToClaimsSystem)) or.filterBoolean('flag.sendToClaimsSystem', authStatusSearchFilters.sendToClaimsSystem);
            });
        }

        if(!isNullOrUndefined(authStatusSearchFilters.isDetermination)) query.filterBoolean('determination.isDetermination', authStatusSearchFilters.isDetermination);
        if(!isNullOrUndefined(authStatusSearchFilters.providerChangesRequested)) query.filterBoolean('flag.providerChangesRequested', authStatusSearchFilters.providerChangesRequested);

        // additional status filters
        if(authStatusSearchFilters.status && authStatusSearchFilters.status.trim().length > 0) query.filterString('description', authStatusSearchFilters.status, 'contains');
        if(authStatusSearchFilters.transitions && authStatusSearchFilters.transitions.length > 0) {
            const statusIds: string[] = authStatusSearchFilters.transitions.map(t => t.id!);
            query.filterExists('transitions', (t => t.filterIdentifierArray('id', statusIds, 'eq')));
        }

        if(authStatusSearchFilters.determination?.result?.approved) query.filterString('determination.result', 'approved');
        if(authStatusSearchFilters.determination?.result?.denied) query.filterString('determination.result', 'denied');
        if(authStatusSearchFilters.determination?.result?.modified) query.filterString('determination.result', 'modified');
        if(authStatusSearchFilters.determination?.result?.other) query.filterString('determination.result', 'other');

        return await this.api.get(query);
    }

    public async loadAuthServiceStatuses(options?: Partial<IQueryLoadOptions>, includes: string[] = [], filters: IAuthServiceStatusSearchFilters = {}, fields: string[] = []): Promise<IApiQueryResults<UM.AuthServiceStatus>> {
        const query = this.api
            .query('um/auths/services/statuses')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(filters.withReasons) {
            query.includeWithState(options, 'reasons')
                .filterExists('reasons');

            if(!includes.includes('reasons')) {
                includes.push('reasons');
            }
        }

        if(!filters.multiFlagFilter) {
            if(!isNullOrUndefined(filters.isInitial)) query.filterBoolean('flag.isInitial', filters.isInitial);
            if(!isNullOrUndefined(filters.isOpen)) query.filterBoolean('flag.isOpen', filters.isOpen);
            if(!isNullOrUndefined(filters.isApproval)) query.filterBoolean('flag.isApproval', filters.isApproval);
            if(!isNullOrUndefined(filters.isLocked)) query.filterBoolean('flag.isLocked', filters.isLocked);
            if(!isNullOrUndefined(filters.requiresConfirmation)) query.filterBoolean('flag.requiresConfirmation', filters.requiresConfirmation);
            if(!isNullOrUndefined(filters.excludeFromExport)) query.filterBoolean('flag.excludeFromExport', filters.excludeFromExport);
            if(!isNullOrUndefined(filters.allowForDeterminationRules)) query.filterBoolean('flag.allowForDeterminationRules', filters.allowForDeterminationRules);
            if(!isNullOrUndefined(filters.isTurnAroundTimeBase)) query.filterBoolean('flag.isTurnAroundTimeBase', filters.isTurnAroundTimeBase);
            if(!isNullOrUndefined(filters.allowStatusDateEdit)) query.filterBoolean('flag.allowStatusDateEdit', filters.allowStatusDateEdit);
            if(!isNullOrUndefined(filters.isVoided)) query.filterBoolean('flag.isVoided', filters.isVoided);
            if(!isNullOrUndefined(filters.sendToClaimsSystem)) query.filterBoolean('flag.sendToClaimsSystem', filters.sendToClaimsSystem);
            if(!isNullOrUndefined(filters.providerChangesRequested)) query.filterBoolean('flag.providerChangesRequested', filters.providerChangesRequested);
        } else {
            query.filterOr(or => {
                if(!isNullOrUndefined(filters.isInitial)) or.filterBoolean('flag.isInitial', filters.isInitial);
                if(!isNullOrUndefined(filters.isOpen)) or.filterBoolean('flag.isOpen', filters.isOpen);
                if(!isNullOrUndefined(filters.isApproval)) or.filterBoolean('flag.isApproval', filters.isApproval);
                if(!isNullOrUndefined(filters.isLocked)) or.filterBoolean('flag.isLocked', filters.isLocked);
                if(!isNullOrUndefined(filters.requiresConfirmation)) or.filterBoolean('flag.requiresConfirmation', filters.requiresConfirmation);
                if(!isNullOrUndefined(filters.excludeFromExport)) or.filterBoolean('flag.excludeFromExport', filters.excludeFromExport);
                if(!isNullOrUndefined(filters.allowForDeterminationRules)) or.filterBoolean('flag.allowForDeterminationRules', filters.allowForDeterminationRules);
                if(!isNullOrUndefined(filters.isTurnAroundTimeBase)) or.filterBoolean('flag.isTurnAroundTimeBase', filters.isTurnAroundTimeBase);
                if(!isNullOrUndefined(filters.allowStatusDateEdit)) or.filterBoolean('flag.allowStatusDateEdit', filters.allowStatusDateEdit);
                if(!isNullOrUndefined(filters.isVoided)) or.filterBoolean('flag.isVoided', filters.isVoided);
                if(!isNullOrUndefined(filters.sendToClaimsSystem)) or.filterBoolean('flag.sendToClaimsSystem', filters.sendToClaimsSystem);
                if(!isNullOrUndefined(filters.providerChangesRequested)) or.filterBoolean('flag.providerChangesRequested', filters.providerChangesRequested);
            });
        }

        if(!isNullOrUndefined(filters.isDetermination)) query.filterBoolean('authStatus.determination.isDetermination', filters.isDetermination);

        // additional status filters
        if(filters.status && filters.status.trim().length > 0) query.filterString('description', filters.status, 'contains');
        if(filters.transitions && filters.transitions.length > 0) {
            const statusIds: string[] = filters.transitions.map(t => t.id!);
            query.filterExists('transitions', (t => t.filterIdentifierArray('id', statusIds, 'eq')));
        }

        if(filters.determination?.result?.approved) query.filterString('determination.status.determination.result', 'approved');
        if(filters.determination?.result?.denied) query.filterString('determination.status.determination.result', 'denied');
        if(filters.determination?.result?.modified) query.filterString('determination.status.determination.result', 'modified');
        if(filters.determination?.result?.other) query.filterString('determination.status.determination.result', 'other');

        if(filters.determination?.statuses && filters.determination.statuses.length > 0) {
            const determinationStatusIds: string[] = filters.determination.statuses.map(t => t.id!);
            query.filterIdentifierArray('determination.status.id', determinationStatusIds, 'eq');
        }

        return await this.api.get(query);
    }

    public async loadAuthMedicationStatuses(options?: Partial<IQueryLoadOptions>, includes: string[] = [], filters: IAuthMedicationStatusSearchFilters = {}, fields: string[] = []): Promise<IApiQueryResults<UM.AuthMedicationStatus>> {
        const query = this.api
            .query('um/auths/medications/statuses')
            .applyStandardLoadOptions(options);

        if(filters.withReasons) {
            query.includeWithState(options, 'reasons')
                .filterExists('reasons');

            if(!includes.includes('reasons')) includes.push('reasons');
            if(!fields.includes('reasons.code')) fields.push('reasons.code');
            if(!fields.includes('reasons.description')) fields.push('reasons.description');
        }

        if(!filters.multiFlagFilter) {
            if(!isNullOrUndefined(filters.isInitial)) query.filterBoolean('flag.isInitial', filters.isInitial);
            if(!isNullOrUndefined(filters.isOpen)) query.filterBoolean('flag.isOpen', filters.isOpen);
            if(!isNullOrUndefined(filters.isApproval)) query.filterBoolean('flag.isApproval', filters.isApproval);
            if(!isNullOrUndefined(filters.isVoided)) query.filterBoolean('flag.isVoided', filters.isVoided);
            if(!isNullOrUndefined(filters.isTurnAroundTimeBase)) query.filterBoolean('flag.isTurnAroundTimeBase', filters.isTurnAroundTimeBase);
        } else {
            query.filterOr(or => {
                if(!isNullOrUndefined(filters.isInitial)) or.filterBoolean('flag.isInitial', filters.isInitial);
                if(!isNullOrUndefined(filters.isOpen)) or.filterBoolean('flag.isOpen', filters.isOpen);
                if(!isNullOrUndefined(filters.isApproval)) or.filterBoolean('flag.isApproval', filters.isApproval);
                if(!isNullOrUndefined(filters.isVoided)) or.filterBoolean('flag.isVoided', filters.isVoided);
                if(!isNullOrUndefined(filters.isTurnAroundTimeBase)) or.filterBoolean('flag.isTurnAroundTimeBase', filters.isTurnAroundTimeBase);
            })
        }

        // additional status filters
        if(!isNullOrUndefined(filters.isDetermination)) query.filterBoolean('determination.isDetermination', filters.isDetermination);
        if(filters.status && filters.status.trim().length > 0) query.filterString('description', filters.status, 'contains');
        if(filters.transitions && filters.transitions.length > 0) {
            const statusIds: string[] = filters.transitions.map(t => t.id!);
            query.filterExists('transitions', (t => t.filterIdentifierArray('id', statusIds, 'eq')));
        }

        if(filters.determination?.result?.approved) query.filterString('determination.status.determination.result', 'approved');
        if(filters.determination?.result?.denied) query.filterString('determination.status.determination.result', 'denied');
        if(filters.determination?.result?.modified) query.filterString('determination.status.determination.result', 'modified');
        if(filters.determination?.result?.other) query.filterString('determination.status.determination.result', 'other');

        if(filters.determination?.statuses && filters.determination.statuses.length > 0) {
            const determinationStatusIds: string[] = filters.determination.statuses.map(t => t.id!);
            query.filterIdentifierArray('determination.status.id', determinationStatusIds, 'eq');
        }

        query.include(...includes).field(...fields);

        return await this.api.get(query);
    }

    public async loadVoidedAuthStatuses(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthStatus>> {

        const query = this.api
            .query('um/auths/statuses')
            .applyStandardLoadOptions(options)
            .include(...includes);

        query.filterBoolean('flag.isVoided', true);
        query.filterBoolean('determination.isDetermination', true);
        query.filterString('determination.result', 'other', 'eq');

        return await this.api.get(query);
    }

    public async loadAuthProviderHistory(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthProviderHistory>> {
        const query = this.api
            .query('um/auths/history/providers')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('auth.id', authId);

        return await this.api.get(query);
    }

    public async loadAuthMedicalReviews(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthMedicalReview>> {
        const query = this.api
            .query(`um/auths/${authId}/medicalreviews`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterOr(or => or
                .filterBoolean('service.isActive', true)
                .filterBoolean('diagnosis.isActive', true)
            );

        return await this.api.get(query);
    }

    public async loadAuthMedicalReviewHistory(filters: IAuthMedicalReviewHistoryFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthMedicalReviewHistory>> {
        const query = this.api
            .query('um/auths/history/medicalreviews')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)

        if(filters.authId) {
            query.filterIdentifier('medicalreview.auth.id', filters.authId);
        }
        if(filters.authServiceId) {
            query.filterIdentifier('medicalreview.service.id', filters.authServiceId);
        }
        if(filters.authDiagnosisId) {
            query.filterIdentifier('medicalreview.diagnosis.id', filters.authDiagnosisId);
        }

        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////
    public async loadAuth(authId: string, includes: string[] = [], options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query(`um/auths/${authId}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthService(serviceId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthService>> {
        const query = this.api
            .query(`um/auths/services/${serviceId}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthDiagnosis(diagnosisId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthDiagnosis>> {
        const query = this.api
            .query(`um/auths/diagnoses/${diagnosisId}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadDuplicateAuthsByService(authId: string, isDental: boolean, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.Auth>> {        //TODO Consider refactoring this into /um/auths/authId/services/serviceId
        const query = this.api
            .query(isDental ? `um/auths/${authId}/duplicates/dentalserviceauths` : `um/auths/${authId}/duplicates/serviceauths`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthClasses(options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeInactiveSubClasses: boolean = false, fields: string[] = []): Promise<IApiQueryResults<UM.AuthClass>> {
        const query = this.api
            .query('um/auths/classes')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(includeInactiveSubClasses) query.includeWithState('inactive', 'subClasses');

        return await this.api.get(query);
    }

    public async loadAuthTypes(isInpatient: boolean, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthType>> {
        const query = this.api
            .query('um/auths/types')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(!isInpatient) {
            query.filterIdentifier('flag.isInpatientOnly', 'false');
        }

        return await this.api.get(query);
    }

    public async loadAuthAdmissionHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthAdmissionHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/admissions`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAuthSubClass(authSubClassId: string, includes: string[] = []): Promise<IApiQueryResults<UM.AuthSubClass>> {
        const query = this.api
            .query(`um/auths/subclasses/${authSubClassId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAuthSubClasses(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthSubClass>> {
        const query = this.api
            .query('um/auths/subclasses/')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAuthSubClassesByClass(classId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.AuthSubClass>> {
        const query = this.api
            .query(`um/auths/classes/${classId}/subClasses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthTypeClassHistoryByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthTypeClassHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/typeClasses`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadEagleTransactionsByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.EagleTransaction>> {
        const query = this.api
            .query(`um/auths/${authId}/eagleTransactions`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadLatestEagleTransactionByAuth(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.EagleTransaction>> {
        const query = this.api
            .query(`um/eagletransactions/latest`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        query.filterIdentifier('auth.id', authId);

        return await this.api.get(query);
    }

    public createLoadQueueQuery(queueId: string, includes: string[] = []): ApiQuery {
        const query = this.api
            .query(`um/auths/queues/${queueId}`)
            .include(...includes);

        return query;
    }
    public async loadQueue(queueId: string, includes: string[] = []): Promise<IApiQueryResults<UM.AuthQueue>> {
        const query = this.createLoadQueueQuery(queueId, includes);

        return await this.api.get(query);
    }

    public async loadPortalQueue(queueId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<UM.PortalQueue>> {
        const query = this.api
            .query(`um/portal/queues/${queueId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadChiefComplaint(authId: string, fields: string[] = []): Promise<IApiQueryResults<UM.AuthChiefComplaint>> {
        const query = this.api
            .query('um/auths/chiefComplaints')
            .filterIdentifier('id', authId)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadChiefComplaintHistory(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthChiefComplaintHistory>> {
        const query = this.api
            .query(`um/auths/history/chiefComplaints`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('auth.id', authId);

        return await this.api.get(query);
    }

    public async loadMemberHistory(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthMemberHistory>> {
        const query = this.api
            .query(`um/auths/history/members`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterIdentifier('auth.id', authId);

        return await this.api.get(query);
    }

    public async loadAuthStatusHistory(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<UM.AuthStatusHistory>> {
        const query = this.api
            .query(`um/auths/${authId}/history/statuses`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAlternateAuthSummary(authId: string, includes: string[] = []): Promise<IApiQueryResults<UM.Auth>> {
        const query = this.api
            .query(`um/auths/${authId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    public async activateQueue(queueId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/queues/${queueId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activatePortalQueue(portalQueueId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/portal/queues/${portalQueueId}`, { isActive });

        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async initializeAuth(authId: string): Promise<IApiActionResults> {
        const action = this.api.action('initialize', `um/auths/${authId}`, {});
        return await this.api.post(action);
    }

    public async insertAuth(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('insert', `um/auths`, auth);
        return await this.api.post(action);
    }

    public async saveAuth(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('update', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async copyAuth(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('copy', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async assignAuth(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('reassign', `um/auths/${auth.id}`, auth);

        return await this.api.post(action);
    }

    public async saveAuthService(service: UM.AuthService): Promise<IApiActionResults> {
        const actionName = !service.id ? 'insert' : 'update';
        const url = `um/auths/services/${service.id ? `${service.id}` : ``}`;
        const action = this.api.action(actionName, url, service);
        return await this.api.post(action);
    }

    public async applyAuthServices(payload: IApplyAuthServicePayload): Promise<IApiActionResults> {
        const action = this.api.action('apply', `um/auths/services`, payload);
        return await this.api.post(action);
    }

    public saveAuthServiceActionInfo(change: IServiceChange): IActionInfo {
        if(!change.service.auth?.id) throw new Error('Missing service.auth.id for change action info');

        if(change.type !== ActionOptions.Update && change.type !== ActionOptions.Insert) throw new Error('Invalid action type for service change action info');

        const url = `um/auths/services/${change.service.id ? `${change.service.id}` : ``}`;

        const body: UM.AuthService = {
            auth: {
                id: change.service.auth.id
            }
        };

        if(change.type === ActionOptions.Insert) {
            if(change.service.definition) body.definition = getReference(change.service.definition);
        } else {
            if(!!change.service.id) body.id = change.service.id;
            if(!isNullOrUndefined(change.service.quantity?.requested)) {
                body.quantity = {
                    requested: change.service.quantity.requested
                };
            }
            if(!isNullOrUndefined(change.service.charge?.requested)) {
                body.charge = {
                    requested: change.service.charge.requested
                };
            }
            if(!!change.service.authorizedDate) body.authorizedDate = change.service.authorizedDate;
            if(!!change.service.expirationDate) body.expirationDate = change.service.expirationDate;

            if(!!change.service.modifiers?.length) {
                body.modifiers = getReferences(change.service.modifiers);
            } else {
                body.modifiers = [];
            }

            // dental
            if(!!change.service.bodySite) body.bodySite = getReference(change.service.bodySite);
            if(!!change.service.bodySubSite) body.bodySubSite = getReference(change.service.bodySubSite);
        }

        const actionInfo: IActionInfo = {
            id: change.service.id,
            action: !change.service.id ? ActionOptions.Insert : ActionOptions.Update,
            method: MethodOptions.Post,
            url: url,
            body: body
        };
        return actionInfo;
    }

    public async deactivateAuthService(service: UM.AuthService, reason: string): Promise<IApiActionResults> {
        const action = this.api.action('deactivate', `um/auths/services/${service.id}`, {}).withReason(reason);
        return await this.api.delete(action);
    }

    public activateAuthServiceActionInfo(change: IServiceChange, isActive: boolean, reason: string): IActionInfo {
        const url = `um/auths/services/${change.service.id}`;
        const actionInfo: IActionInfo = {
            id: change.service.id,
            action: isActive ? ActionOptions.Reactivate : ActionOptions.Deactivate,
            method: isActive ? MethodOptions.Post : MethodOptions.Delete,
            url: url,
            reason: reason
        };
        return actionInfo;
    }

    public async insertAuthDiagnosis(authDiagnosis: UM.AuthDiagnosis): Promise<IApiActionResults> {
        const action = this.api.action('insert', `um/auths/diagnoses`, authDiagnosis);
        return await this.api.post(action);
    }

    public async activateAuthDiagnosis(authDiagnosisId: string, isActive: boolean, reason: string): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/diagnoses/${authDiagnosisId}`, { isActive: isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action.withReason(reason));
    }

    public async updateAuthDiagnosisExclusive(authDiagnosis: UM.AuthDiagnosis): Promise<IApiActionResults> {
        const action = this.api.action('updateExclusive', `um/auths/diagnoses/${authDiagnosis.id}`, authDiagnosis);
        return await this.api.post(action);
    }

    public async insertAuthMedication(authMedication: UM.AuthMedication): Promise<IApiActionResults> {
        const action = this.api.action('insert', `um/auths/medications`, authMedication);
        return await this.api.post(action);
    }

    public async activateAuthMedication(authMedicationId: string, isActive: boolean, reason: string): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/medications/${authMedicationId}`, { isActive: isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action.withReason(reason));
    }

    public activateAuthMedicationActionInfo(change: IMedicationChange, isActive: boolean, reason: string): IActionInfo {
        const url = `um/auths/medications/${change.medication.id}`;
        const actionInfo: IActionInfo = {
            id: change.medication.id,
            action: isActive ? ActionOptions.Reactivate : ActionOptions.Deactivate,
            method: isActive ? MethodOptions.Post : MethodOptions.Delete,
            url: url,
            reason: reason
        };
        return actionInfo;
    }

    public async updateAuthMedication(authMedication: UM.AuthMedication): Promise<IApiActionResults> {
        const action = this.api.action('update', `um/auths/medications/${authMedication.id}`, authMedication);
        return await this.api.post(action);
    }

    public saveAuthMedicationActionInfo(change: IMedicationChange): IActionInfo {
        if(!change.medication.auth?.id) throw new Error('Missing medication.auth.id for change action info');

        if(change.type !== ActionOptions.Update && change.type !== ActionOptions.Insert) throw new Error('Invalid action type for medication change action info');

        const url = `um/auths/medications/${change.medication.id ? `${change.medication.id}` : ``}`;

        const body: UM.AuthMedication = {
            auth: {
                id: change.medication.auth.id
            }
        };

        if(change.type === ActionOptions.Insert) {
            if(change.medication.definition) body.definition = getReference(change.medication.definition);
        } else {
            if(!!change.medication.id) body.id = change.medication.id;
            if(!!change.medication.quantity?.requested) {
                body.quantity = {};
                body.quantity.requested = {};
                if(!isNullOrUndefined(change.medication.quantity.requested.value)) body.quantity.requested.value = change.medication.quantity.requested.value;
                if(!!change.medication.quantity.requested.unit) body.quantity.requested.unit = getReference(change.medication.quantity.requested.unit);
            }
            if(!!change.medication.charge?.requested) {
                body.charge = {
                    requested: change.medication.charge.requested
                };
            }
            if(!!change.medication.authorizedDate) body.authorizedDate = change.medication.authorizedDate;
            if(!!change.medication.expirationDate) body.expirationDate = change.medication.expirationDate;
            if(!isNullOrUndefined(change.medication.days?.requested)) {
                body.days = {
                    requested: change.medication.days.requested
                };
            }
        }

        const actionInfo: IActionInfo = {
            id: change.medication.id,
            action: !change.medication.id ? ActionOptions.Insert : ActionOptions.Update,
            method: MethodOptions.Post,
            url: url,
            body: body
        };
        return actionInfo;
    }

    public async applyAuthMedications(payload: IApplyAuthMedicationPayload): Promise<IApiActionResults> {
        const action = this.api.action('apply', `um/auths/medications`, payload);
        return await this.api.post(action);
    }

    public async insertAuthDetail(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('insertDetails', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async deleteAuthDetail(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('deleteDetails', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async updateAuthServiceExclusive(service: UM.AuthService): Promise<IApiActionResults> {
        const action = this.api.action('updateExclusive', `um/auths/services/${service.id}`, service);
        return await this.api.post(action);
    }

    public async updateAuthDetail(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('updateDetail', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async saveAuthPreferredContact(contact: UM.AuthPreferredContact): Promise<IApiActionResults> {
        const action = this.api.action('save', `UM/Auths/PreferredContacts`, contact);
        return await this.api.post(action);
    }

    public saveAuthPreferredContactActionInfo(contact: UM.AuthPreferredContact): IActionInfo {
        const url = `UM/Auths/PreferredContacts`;

        const actionInfo: IActionInfo = {
            action: ActionOptions.Save,
            method: MethodOptions.Post,
            url: url,
            body: contact
        };
        return actionInfo;
    }

    public async saveChiefComplaint(chiefComplaint: UM.AuthChiefComplaint): Promise<IApiActionResults> {
        const action = this.api.action('save', `um/auths/chiefComplaints`, chiefComplaint);
        return await this.api.post(action);
    }

    public async saveAuthStatus(auth: UM.Auth, applicationEventCode: string | null = null): Promise<IApiActionResults> {
        const action = this.api.action('updateStatus', `um/auths/${auth.id}`, auth);
        if(applicationEventCode) return await this.api.post(action.withApplicationEventCode(applicationEventCode));
        return await this.api.post(action);
    }

    public async saveAuthStatusReason(statusReason: UM.AuthStatusReason): Promise<IApiActionResults> {
        const actionName = !statusReason.id ? 'insert' : 'update';
        const url = `um/auths/statuses/reasons/${statusReason.id ? `${statusReason.id}` : ``}`;
        const action = this.api.action(actionName, url, statusReason);
        return await this.api.post(action);
    }

    public async saveAuthServiceStatusReason(statusReason: UM.AuthServiceStatusReason): Promise<IApiActionResults> {
        const actionName = !statusReason.id ? 'insert' : 'update';
        const url = `um/auths/services/statuses/reasons/${statusReason.id ? `${statusReason.id}` : ``}`;
        const action = this.api.action(actionName, url, statusReason);
        return await this.api.post(action);
    }

    public async saveAuthMedicationStatusReason(statusReason: UM.AuthMedicationStatusReason): Promise<IApiActionResults> {
        const actionName = !statusReason.id ? 'insert' : 'update';
        const url = `um/auths/medications/statuses/reasons/${statusReason.id ? `${statusReason.id}` : ``}`;
        const action = this.api.action(actionName, url, statusReason);
        return await this.api.post(action);
    }

    public async saveAuthServiceTurnAroundTime(turnAroundTime: UM.AuthServiceTurnAroundTime): Promise<IApiActionResults> {
        const actionName = !turnAroundTime.id ? 'insert' : 'update';
        const url = `um/auths/services/turnAroundTimes/${turnAroundTime.id ? `${turnAroundTime.id}` : ``}`;
        const action = this.api.action(actionName, url, turnAroundTime);
        return await this.api.post(action);
    }

    public async completeEntry(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('completeEntry', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async routeToQueue(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('route', `um/auths/${auth.id}`, auth);

        return await this.api.post(action);
    }

    public async returnFromQueue(authId: string, queueId: string): Promise<IApiActionResults> {
        const auth: UM.Auth = {
            id: authId,
            queues: [{ id: queueId }]
        }
        const action = this.api.action('completeRoute', `um/auths/${auth.id}`, auth);

        return await this.api.post(action);
    }

    public async saveAuthHistory(history: UM.AuthStatusHistory): Promise<IApiActionResults> {
        const action = this.api.action('update', `um/auths/history/statuses/${history.id}`, history);
        return await this.api.post(action);
    }

    public async autoDetermination(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('AutoDetermination', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async triggerCorrespondence(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('trigger', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }

    public async triggerDetailCorrespondence(authId: string, services: UM.AuthService[]): Promise<IApiActionResults> {
        const action = this.api.action('trigger', `um/auths/${authId}`, { id: authId, flag: { serviceLineOnly: true }, services: services } as UM.Auth);
        return await this.api.post(action);
    }

    public async triggerServiceCorrespondence(authId: string, services: UM.AuthService[]): Promise<IApiActionResults> {
        const action = this.api.action('triggerServiceCorrespondence', `um/auths/${authId}`, { id: authId, services: services } as UM.Auth);
        return await this.api.post(action);
    }

    public async triggerQueueRouting(auth: UM.Auth, applicationEventCode: string | null = null): Promise<IApiActionResults> {
        const action = this.api.action('TriggerQueueRouting', `um/auths/${auth.id}`, auth);
        if(applicationEventCode) return await this.api.post(action.withApplicationEventCode(applicationEventCode));
        return await this.api.post(action);
    }

    public async verifyAssignment(userId: string, auths: UM.Auth[]): Promise<IApiFilterResults> {
        const body = { filter: { model: 'um.auth', items: auths } };
        const action = this.api.action('filter', `users/${userId}`, body);

        return await this.api.post(action);
    }

    public async verifyAssignmentByMember(userId: string, memberId: string): Promise<IApiFilterResults> {
        const body = { filter: { model: 'um.member', items: [{ id: memberId }] } };
        const action = this.api.action('filter', `users/${userId}`, body);

        return await this.api.post(action);
    }

    public async verifyExternalSystemResult(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('externalSystemResult', `um/auths/${auth.id}`, auth);

        return await this.api.post(action);
    }

    public async finalizeAuth(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('finalize', `um/auths/${auth.id}`, auth);

        return await this.api.post(action);
    }

    public async saveQueue(queue: UM.AuthQueue): Promise<IApiActionResults> {
        const actionName = !queue.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `um/auths/queues/${actionName === 'update' ? queue.id : ''}`, queue);

        return await this.api.post(action);
    }

    public async savePortalQueue(portalQueue: UM.PortalQueue): Promise<IApiActionResults> {
        const actionName = !portalQueue.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `um/portal/queues/${actionName === 'update' ? portalQueue.id : ''}`, portalQueue);

        return await this.api.post(action);
    }

    public async saveAuthFlag(authFlag: UM.AuthFlag): Promise<IApiActionResults> {
        const actionName = !authFlag.id ? 'insert' : 'update';
        const url = `um/auths/flags/${authFlag.id ? `${authFlag.id}` : ``}`;
        const action = this.api.action(actionName, url, authFlag);
        return await this.api.post(action);
    }

    public async insertAuthFlagDetails(authFlag: UM.AuthFlag): Promise<IApiActionResults> {
        const action = this.api.action('insertDetails', `um/auths/flags/${authFlag.id}`, authFlag);
        return await this.api.post(action);
    }

    public async deleteAuthFlagDetail(authFlag: UM.AuthFlag): Promise<IApiActionResults> {
        const action = this.api.action('deleteDetails', `um/auths/flags/${authFlag.id}`, authFlag);
        return await this.api.post(action);
    }

    public async activateAuthFlag(authFlagId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/flags/${authFlagId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateAuthServiceTurnAroundTime(turnAroundTimeId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/services/turnAroundTimes/${turnAroundTimeId}`, { isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async activateAuthStatus(authStatusId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/statuses/${authStatusId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateAuthServiceStatus(authServiceStatusId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/services/statuses/${authServiceStatusId}`, { isActive: isActive });
        if(isActive) return await this.api.post(action);
        return await this.api.delete(action);
    }

    public async activateAuthMedicationStatus(authMedicationStatusId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/auths/medications/statuses/${authMedicationStatusId}`, { isActive: isActive });
        if(isActive) return await this.api.post(action);
        return await this.api.delete(action);
    }

    public async saveStatus(authStatus: UM.AuthStatus): Promise<IApiActionResults> {
        const actionName = !authStatus.id ? 'insert' : 'update';
        const url = `um/auths/statuses/${authStatus.id ? `${authStatus.id}` : ``}`;
        const action = this.api.action(actionName, url, authStatus);
        return await this.api.post(action);
    }

    public async saveServiceStatus(status: UM.AuthServiceStatus): Promise<IApiActionResults> {
        const actionName = !status.id ? 'insert' : 'update';
        const url = `um/auths/services/statuses/${status.id ? `${status.id}` : ``}`;
        const action = this.api.action(actionName, url, status);
        return await this.api.post(action);
    }

    public async saveMedicationStatus(status: UM.AuthMedicationStatus): Promise<IApiActionResults> {
        const actionName = !status.id ? 'insert' : 'update';
        const url = `um/auths/medications/statuses/${status.id ? `${status.id}` : ``}`;
        const action = this.api.action(actionName, url, status);
        return await this.api.post(action);
    }

    public async updateStatusExclusive(authStatus: UM.AuthStatus): Promise<IApiActionResults> {
        const url = `um/auths/statuses/${authStatus.id}`;
        const action = this.api.action('updateExclusive', url, authStatus);
        return await this.api.post(action);
    }

    public async updateServiceStatusExclusive(status: UM.AuthServiceStatus): Promise<IApiActionResults> {
        const url = `um/auths/services/statuses/${status.id}`;
        const action = this.api.action('updateExclusive', url, status);
        return await this.api.post(action);
    }

    public async updateMedicationStatusExclusive(status: UM.AuthMedicationStatus): Promise<IApiActionResults> {
        const url = `um/auths/medications/statuses/${status.id}`;
        const action = this.api.action('updateExclusive', url, status);
        return await this.api.post(action);
    }

    public async activateAuthStatusReason(authStatusReasonId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `um/auths/statuses/reasons/${authStatusReasonId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateAuthServiceStatusReason(authServiceStatusReasonId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `um/auths/services/statuses/reasons/${authServiceStatusReasonId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive });
        if(isActive) return await this.api.post(action);
        return await this.api.delete(action);
    }

    public async activateAuthMedicationStatusReason(authMedicationStatusReasonId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `um/auths/medications/statuses/reasons/${authMedicationStatusReasonId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive });
        if(isActive) return await this.api.post(action);
        return await this.api.delete(action);
    }

    public async insertCommonDiagnosesBatch(diagnoses: CD.Diagnosis[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const d of diagnoses) {
            batch.post(this.api.action('insert', 'um/commonDiagnoses', d));
        }
        return await this.api.execute(batch, { progress });
    }

    public async deleteCommonDiagnosis(commonDiagnosis: UM.CommonDiagnosis): Promise<IApiActionResults> {
        const action = this.api.action("delete", `um/commonDiagnoses/${commonDiagnosis.id}`, {});
        return await this.api.delete(action);
    }

    public async deleteCommonService(commonService: UM.CommonService): Promise<IApiActionResults> {
        const action = this.api.action('delete', `um/commonServices/${commonService.id}`, {});
        return await this.api.delete(action);
    }

    public async insertCommonServicesBatch(services: CD.Service[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const s of services) {
            batch.post(this.api.action('insert', 'um/commonServices', s));
        }
        return await this.api.execute(batch, { progress });
    }

    public async validateAuth(auth: UM.Auth): Promise<IApiActionResults> {
        const action = this.api.action('validate', `um/auths/${auth.id}`, auth);
        return await this.api.post(action);
    }
}
